import type { BlocksContent } from "@strapi/blocks-react-renderer";
import clsx from "clsx";
import { type ComponentProps, useCallback, useState } from "react";

import { CommonBlocksRenderer } from "@widgets/common-blocks-renderer";

import type { TableBlockType } from "@features/article-content/model";
import { Breadcrumbs } from "@features/navigations/breadcrumbs";
import { SizesPopup } from "@features/sizes-popup/sizes-popup";

import { COLORS_VALUES } from "@shared/constants";
import { Accordion } from "@shared/ui/accordion/accordion";
import { Button } from "@shared/ui/button";
import { ButtonWithIcon } from "@shared/ui/button-with-icon";
import { Container } from "@shared/ui/container";
import { Popup } from "@shared/ui/popup";
import { Body, Heading } from "@shared/ui/typography";
import { formatNumberWithSpaces } from "@shared/utils/string";

import type { MerchType } from "../../model";
import type { MerchVariantType } from "../../model/types";

import s from "./content.module.scss";

export type CompProps = ComponentProps<"div"> & {
  className?: string;
  currentVariant: MerchVariantType;
  data: MerchType;
  onVariantChange: (id: number) => void;
  popupData: {
    tableSize: TableBlockType;
    popup: {
      title: string;
      description: string;
    };
  };
};

export const Content = (props: CompProps) => {
  const { className, data, currentVariant, onVariantChange, popupData } = props;

  const [activeAccordion, setActiveAccordion] = useState<string | null>(null);
  const [isOpenPopup, setOpenPopup] = useState(false);

  const handleClosePopup = useCallback(() => {
    setOpenPopup(false);
  }, []);

  const handleOpenPopup = useCallback(() => {
    setOpenPopup(true);
  }, []);

  const handleOpenAccordion = useCallback((id: string) => {
    setActiveAccordion((prev) => (prev === id ? null : id));
  }, []);

  return (
    <Container className={clsx(s.root, className)}>
      <div className={s.wrapper}>
        <Breadcrumbs className={s.breadcrumbs} items={["shop"]} />
        <div className={s.titleInfo}>
          {data?.title && (
            <Heading level={2} className={s.title}>
              {data?.title}
            </Heading>
          )}
          {data?.description && <Body size="caption">{data?.description}</Body>}
        </div>
        <div className={s.lines}>
          {currentVariant?.sizes && (
            <div className={s.line}>
              <Body size="uppercase" weight="semiBold" className={s.subtitle}>
                Размер {currentVariant.sizes}
              </Body>
              {data.isViewSizeTable && (
                <Button className={s.tableBtn} onClick={handleOpenPopup}>
                  <Body size="uppercase-small" weight="semiBold">
                    Таблица размеров
                  </Body>
                </Button>
              )}
            </div>
          )}
          {data?.merchVariations?.length > 0 && (
            <div className={s.line}>
              <Body size="uppercase" weight="semiBold" className={s.subtitle}>
                Цвет
              </Body>
              <div className={s.colors}>
                {data?.merchVariations?.map((variant) => (
                  <Button
                    key={variant.id}
                    onClick={() => onVariantChange(variant.id)}
                    className={clsx(s.colorButton, {
                      [s.activeVariant]: variant.id === currentVariant.id,
                    })}
                    style={
                      {
                        "--variant-color": variant?.color?.value,
                      } as React.CSSProperties
                    }
                  >
                    <div />
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className={s.priceWrapper}>
          <Heading level={5}>
            {currentVariant?.price
              ? formatNumberWithSpaces(currentVariant.price)
              : ""}
          </Heading>
          {data.link && (
            <ButtonWithIcon
              variant="secondary-dark"
              href={data.link}
              className={s.mainBtn}
              iconName="sur"
              iconFillColor="cadmium"
            >
              Где купить?
            </ButtonWithIcon>
          )}
        </div>
        <div className={s.detailInfo}>
          {data?.detailInfo && (
            <Accordion
              title="Подробная информация"
              id="detail-info"
              className={s.accordion}
              isOnceOpen
              activeAccordion={activeAccordion}
              onAccordionClick={handleOpenAccordion}
            >
              <div className={s.textWrapper}>
                <CommonBlocksRenderer
                  data={data?.detailInfo as unknown as BlocksContent}
                />
              </div>
            </Accordion>
          )}
          {data?.recomendation && (
            <Accordion
              title="Рекомендации по уходу"
              id="recomendation"
              className={s.accordion}
              isOnceOpen
              activeAccordion={activeAccordion}
              onAccordionClick={handleOpenAccordion}
            >
              <div className={s.textWrapper}>
                <CommonBlocksRenderer
                  data={data?.recomendation as unknown as BlocksContent}
                />
              </div>
            </Accordion>
          )}
          {data?.composition && (
            <Accordion
              title="Состав"
              id="composition"
              className={s.accordion}
              isOnceOpen
              activeAccordion={activeAccordion}
              onAccordionClick={handleOpenAccordion}
            >
              <div className={s.textWrapper}>
                <CommonBlocksRenderer
                  data={data?.composition as unknown as BlocksContent}
                />
              </div>
            </Accordion>
          )}
        </div>
      </div>
      <Popup
        id="sizes-popup"
        isOpen={isOpenPopup}
        onClose={handleClosePopup}
        variant="feedback-sizes"
      >
        <SizesPopup popupData={popupData} />
      </Popup>
    </Container>
  );
};

Content.displayName = "Content";
